import { Component, ErrorHandler, Inject, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { DateService } from '@lemon/core/service/date.service'
import { AsiakasKopioija } from 'app/_angular/service/asiakas/asiakas.kopioija'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { Subject, takeUntil } from 'rxjs'

import { SopimusPalvelupaketti } from '@lemon/core-firebase/model/kayttaja'
import {
  Asiakas,
  AsiakkaanSopimuskausi,
  AsiakkaanSopimusversio,
  PakotaSopimuksenUudelleenAllekirjoitusPyynto,
  PakotaSopimuksenUudelleenAllekirjoitusVastaus,
} from '../../_jaettu-lemonator/model/asiakas'
import { SopimusversioLomake } from './sopimusversio.component'

export interface AsiakkaanSopimuskausiUusiDialogData {
  asiakas: Asiakas
}

interface SopimuskausiLomake {
  alkaa: Date | null
}

@Component({
  templateUrl: './sopimuskausi-uusi.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaanSopimuskausiUusiDialog implements OnInit, OnDestroy {
  // @ViewChild(MatInput, { static: true }) sopimuskausiAlkaaInputti: MatInput

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private _kausi: AsiakkaanSopimuskausi = null

  sopimuskausiLomake: FieldTree<SopimuskausiLomake> = null
  sopimusversioLomake: FieldTree<SopimusversioLomake> = null
  inputname = 'mnbiggteagjopq' + Math.random()
  minimiAlkaaPaivamaara: Date = null

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: AsiakkaanSopimuskausiUusiDialogData,
    private _dialogRef: MatDialogRef<AsiakkaanSopimuskausiUusiDialog>,
    private _dateService: DateService,
    private _firebase: FirebaseLemonator,
    private _validationService: FormValidationService,
    private _asiakasService: AsiakasService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _asiakasKopioija: AsiakasKopioija,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _timestampService: TimestampService,
  ) {
    const viimeisinKausi =
      this._data.asiakas.sopimuskaudet?.length > 0
        ? this._data.asiakas.sopimuskaudet.reduce((prev, curr) => {
            if (this._dateService.compareLocalDates(prev.alkaa, '<', curr.alkaa)) {
              return curr
            }
            return prev
          })
        : null
    // const uusiAlkaa = viimeisinKausi?.loppuu ?? this._dateService.currentLocalDate()
    const uusiAlkaa = this._dateService.lisaaPaiviaPaikallinen(viimeisinKausi?.loppuu ?? this._dateService.currentLocalDate(), 1)
    const sopimusversionAvain = this._firebase.firestoreCreateId()
    const sopimuskausi: AsiakkaanSopimuskausi = {
      avain: this._firebase.firestoreCreateId(),
      alkaa: uusiAlkaa,
      loppuu: null,
      lahetaPaattymassaEmail: null,
      lahetaPaattynytEmail: null,
      tositteidenToimitusLoppuu: null,
      lisatiedot: null,
      lopettamisenSyy: null,
      tauot: null,
      luoja: null,
      luotu: null,
      paivitetty: null,
      paivittaja: null,
      poistettu: false,
      sopimusversiot: [
        {
          avain: sopimusversionAvain,
          voimassaAlkaen: this._dateService.localDateToNumber(uusiAlkaa),
          palvelupaketti: null,
          lisapalvelut: {},
          allekirjoitusStatus: 'ei-allekirjoitettu',
          luoja: null,
          luotu: null,
        },
      ],
      uusinSopimusversio: sopimusversionAvain,
    }

    this._kausi = sopimuskausi

    this.minimiAlkaaPaivamaara = viimeisinKausi?.loppuu ? this._dateService.lisaaPaivia(this._dateService.localDateToDate(viimeisinKausi.loppuu), 1) : new Date(1980, 0, 1)

    this.sopimuskausiLomake = this._annaLomakeSopimuskaudelle(this._kausi)
    this.sopimusversioLomake = this._annaLomakeSopimuskaudenVersiolle(this._kausi.sopimusversiot[0])
  }

  ngOnInit() {
    // setTimeout(() => {
    //   this.sopimuskausiAlkaaInputti.focus()
    // }, 50)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  private _annaLomakeSopimuskaudelle(sopimuskausi: AsiakkaanSopimuskausi): FieldTree<SopimuskausiLomake> {
    const sopimuskausiLomake = form(signal<SopimuskausiLomake>({ alkaa: this._dateService.localDateToDate(sopimuskausi.alkaa) }), polku => {
      required(polku.alkaa)
    })

    arvonMuutokset(sopimuskausiLomake.alkaa)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        sopimuskausi.alkaa = this._dateService.dateToLocalDate(value)
      })

    return sopimuskausiLomake
  }

  private _annaLomakeSopimuskaudenVersiolle(sopimusversio: AsiakkaanSopimusversio): FieldTree<SopimusversioLomake> {
    const sopimusversioLomake = form(
      signal<SopimusversioLomake>({
        palvelupaketti: null,
        palkat: null,
        // 'ennakkoverojenSeuranta': null,
        // 'ostolaskujenMaksaminen': null,
        // 'kirjanpidonReskontra': null,
        // 'myyntilaskujenLahettaminen': null,
        // 'reskontranHoito': null,
        // 'saatavienValvonta': null,
        sopimusversioVoimassaAlkaen: null,
      }),
      polku => {
        required(polku.palvelupaketti)
      },
    )

    arvonMuutokset(sopimusversioLomake.palvelupaketti)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => {
        sopimusversio.palvelupaketti = val
      })
    arvonMuutokset(sopimusversioLomake.palkat)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => {
        sopimusversio.lisapalvelut.PALKAT = val
      })
    // this.sopimusForm.get('ennakkoverojenSeuranta').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   this.sopimusversio.lisapalvelut.ENNAKKOVEROJEN_SEURANTA = val
    // })
    // this.sopimusForm.get('ostolaskujenMaksaminen').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   this.sopimusversio.lisapalvelut.OSTOLASKUJEN_MAKSAMINEN = val
    // })
    // this.sopimusForm.get('kirjanpidonReskontra').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   this.sopimusversio.lisapalvelut.KIRJANPIDON_RESKONTRA = val
    // })
    // this.sopimusForm.get('myyntilaskujenLahettaminen').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   this.sopimusversio.lisapalvelut.MYYNTILASKUJEN_LAHETTAMINEN = val
    // })
    // this.sopimusForm.get('reskontranHoito').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   this.sopimusversio.lisapalvelut.RESKONTRAN_HOITO = val
    // })
    // this.sopimusForm.get('saatavienValvonta').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   this.sopimusversio.lisapalvelut.SAATAVIEN_VALVONTA = val
    // })
    // sopimusversioForm.get('sopimusversioVoimassaAlkaen').valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => {
    //   if (val) {
    //     sopimusversio.voimassaAlkaen = this._dateService.dateToNumber(val)
    //   }
    // })
    return sopimusversioLomake
  }

  async tallenna() {
    if (!this.sopimuskausiLomake().valid() || !this.sopimusversioLomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.sopimuskausiLomake)
      this._validationService.merkitseKokoLomakeKosketuksi(this.sopimusversioLomake)
      return
    }
    this._ladataanService.aloitaLataaminen()

    try {
      const kirjanpitajanTiedot = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()
      this._kausi.luoja = kirjanpitajanTiedot.uid
      this._kausi.paivittaja = kirjanpitajanTiedot.uid
      this._kausi.luotu = this._timestampService.now()
      this._kausi.paivitetty = this._kausi.luotu

      this._kausi.sopimusversiot[0].luoja = this._kausi.luoja
      this._kausi.sopimusversiot[0].luotu = this._kausi.luotu
      this._kausi.sopimusversiot[0].voimassaAlkaen = this._dateService.localDateToNumber(this._kausi.alkaa)

      // Holvi Zen -asiakkailla ei ole Lemonaid-sopimusta, joten sopimusversio merkitään suoraan allekirjoitetuksi.
      const onkoHolviZenPaketti = this._kausi.sopimusversiot[0].palvelupaketti === SopimusPalvelupaketti.HOLVI_ZEN
      if (onkoHolviZenPaketti) {
        this._kausi.sopimusversiot[0].allekirjoitusStatus = 'allekirjoitettu'
      }

      const asiakasCopy = this._asiakasKopioija.kopioiAsiakas(this._data.asiakas)
      if (!asiakasCopy.sopimuskaudet) {
        asiakasCopy.sopimuskaudet = []
      }
      asiakasCopy.sopimuskaudet.push(this._kausi)

      const batch = this._firebase.firestoreBatch()
      await this._asiakasService.lisaaAsiakasBatchiin(asiakasCopy, kirjanpitajanTiedot, 'tilikaudet-eivat-voineet-muuttua', false, false, batch)

      await batch.commit()
      if (!onkoHolviZenPaketti) {
        await this._pakotaSopimuksenAllekirjoittaminenUudelleen(this._data.asiakas)
      }

      this._asiakasService.asetaNykyinenAsiakas(asiakasCopy)
      this._dialogRef.close()
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  naytetaankoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    const tila = kentta()
    return tila.errors().some(virhe => virhe.kind === laji) && (tila.dirty() || tila.touched())
  }

  private _pakotaSopimuksenAllekirjoittaminenUudelleen(asiakas: Asiakas): Promise<any> {
    const pyynto: PakotaSopimuksenUudelleenAllekirjoitusPyynto = {
      asiakasAvain: asiakas.avain,
      naytaKayttajilleTervetuloaDialogi: true,
      vaadiKayttajiltaUusiTunnistautuminen: true,
      vaadiPepTietojenPaivittaminen: true,
      vaadiSopimuksenAllekirjoittaminenUudelleen: true,
    }
    return this._firebase
      .functionsCall<PakotaSopimuksenUudelleenAllekirjoitusPyynto, PakotaSopimuksenUudelleenAllekirjoitusVastaus>('sopimuksenUudelleenAllekirjoittaminenPakotetusti', pyynto)
      .then(() => {})
      .catch(error => {
        this._errorHandler.handleError(error)
      })
  }
}
